import { AmbientLight, Mesh, PointLight, Scene } from 'three'
import { RenderEngine } from './RenderEngine'
import { RenderLoop } from './RenderLoop'
import { DebugUI } from './interface/DebugUI'
import { Sizes } from './Sizes'
import { Camera } from './Camera'
import { Resources } from './Resources'
import { InfoConfig, InfoUI } from './interface/InfoUI'
import { Experience, ExperienceConstructor } from './Experience'
import { Loader } from './interface/Loader'
import { Raycaster } from './Raycaster'
import { Points, Position } from '../models/Points'
import { CSS2DRenderEngine } from './CSS2DRenderEngine'

export class Engine {
  public readonly disable: boolean
  public readonly camera!: Camera
  public readonly scene!: Scene
  public readonly renderEngine!: RenderEngine
  public readonly css2DRenderer!: CSS2DRenderEngine
  public readonly time!: RenderLoop
  public readonly debug!: DebugUI
  public readonly raycaster!: Raycaster
  public readonly infoUI!: InfoUI
  public readonly sizes!: Sizes
  public readonly canvas!: HTMLCanvasElement
  public readonly resources!: Resources
  public readonly experience!: Experience
  public readonly pointsExperience!: Experience
  private readonly loader!: Loader

  constructor({
    canvas,
    experience,
    data,
    info,
    disable = true,
  }: {
    canvas: HTMLCanvasElement
    experience: { model: ExperienceConstructor, building: { path: string, position: Position } }
    data: Array<Object>
    info?: InfoConfig
    disable?: boolean // 点击points是否弹窗
  }) {
    if (!canvas) {
      throw new Error('No canvas provided')
    }
    this.disable = disable
    this.canvas = canvas
    this.sizes = new Sizes(this)
    this.debug = new DebugUI()
    this.time = new RenderLoop(this)
    this.scene = new Scene()
    // 注意顺序 css2DRenderer 在 camera 之前初始化
    this.css2DRenderer = new CSS2DRenderEngine(this)
    this.camera = new Camera(this)
    this.raycaster = new Raycaster(this)
    this.infoUI = new InfoUI(this, info)
    this.renderEngine = new RenderEngine(this)

    // 加载建筑模型
    this.experience = new experience.model(this, experience.building)
    // 加载测点数据
    this.pointsExperience = new Points(this, data)
    // 把建筑模型和测点模型全部添加到 resources 中
    this.resources = new Resources([...this.experience.resources, ...this.pointsExperience.resources])
    this.loader = new Loader(this)



    this.resources.on('loaded', () => {
      console.log('loaded')
      this.experience.init();
      this.pointsExperience.init();
      this.loader.complete()
    })

    this.resources.on('progress', (progress: number) => {
      this.loader.setProgress(progress)
    })

    // 添加环境光
    const ambientLight = new AmbientLight(0xffffff, 0.1); // 白色光，强度为0.5
    this.scene.add(ambientLight);

    const pointLightPositions = [
      [0, 100, 0],
      [0, -100, 0],
      [100, 0, 0],
      [-100, 0, 0],
      [0, 0, 100],
      [0, 0, -100],
      [0, 0, 0] // 中心点 和 3个轴的对称位置
    ];

    pointLightPositions.forEach((pos) => {
      const pointLight = new PointLight(0xffffff, 0.3, 1000); // 强度为 1，距离为 1000
      pointLight.position.set(pos[0], pos[1], pos[2]); // 设置光源位置
      pointLight.castShadow = true; // 开启阴影
      this.scene.add(pointLight);
    });


  }

  update(delta: number) {
    if (!this.loader.isComplete) return
    this.camera.update()
    this.renderEngine.update()
    this.css2DRenderer.update()
    this.experience.update(delta)
    this.pointsExperience.update(delta)
    this.debug.update()
  }

  resize() {
    this.camera.resize()
    this.renderEngine.resize()
    this.css2DRenderer.resize()
    if (this.experience.resize) {
      this.experience.resize()
    }
    if (this.pointsExperience.resize) {
      this.pointsExperience.resize()
    }
  }

  dispose() {
    this.scene.traverse((object) => {
      if (object instanceof Mesh) {
        object.geometry.dispose();
        object.material.dispose();
        this.scene.remove(object);
      }
    });
    this.time.dispose();
  }
}
